import { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { AiGate } from './AiGate';
import { AI_FEATURES } from '../utils/aiFeatures';

const MONTH_LABEL = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtMonth = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const d = new Date(s + 'T00:00:00Z');
  return MONTH_LABEL[d.getUTCMonth()] ?? '';
};

const fmtWeek = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const d = new Date(s + 'T00:00:00Z');
  return `${MONTH_LABEL[d.getUTCMonth()]} W${Math.ceil(d.getUTCDate() / 7)}`;
};

export function GrowthTimeline({ userId, aiDataConsent, onEnableAI }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setErr(null);
    api.get('/api/growth/timeline?limit=6')
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.error || e.message || 'Failed to load'); });
    return () => { cancelled = true; };
  }, [userId]);

  if (err) return <div className="p-6 text-sm text-stone-500">Couldn't load your timeline yet.</div>;
  if (!data) return <div className="p-6 text-sm text-stone-400 animate-pulse">Loading your growth…</div>;
  if (!data.snapshots?.length) {
    return (
      <div className="p-8 text-center text-stone-500">
        <p className="text-sm">Your growth timeline appears after a few weeks of journaling.</p>
      </div>
    );
  }

  const latest = data.snapshots[data.snapshots.length - 1];
  const prev   = data.snapshots.length > 1 ? data.snapshots[data.snapshots.length - 2] : null;
  const vocabDelta = prev ? latest.emotion_word_count - prev.emotion_word_count : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
      <HeaderStats latest={latest} vocabDelta={vocabDelta} monthsCount={data.snapshots.length} />
      <AreaChart weekly={data.weekly || []} />
      <EmotionWords
        newWords={latest.new_words || []}
        growingWords={latest.growing_words || []}
        coreWords={latest.core_words || []}
      />
      <AiGate feature={AI_FEATURES.pairs} consent={aiDataConsent} onEnable={onEnableAI}>
        <LanguagePairs pairs={data.pairs || []} />
      </AiGate>
      {latest.narrative && (
        <p className="text-sm text-stone-600 italic leading-relaxed">{latest.narrative}</p>
      )}
    </div>
  );
}

function HeaderStats({ latest, vocabDelta, monthsCount }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <StatCard
        label="Emotional vocabulary"
        value={`${latest.emotion_word_count} words`}
        hint={vocabDelta != null && vocabDelta > 0 ? `+${vocabDelta} from last month` : `across ${monthsCount} months`}
      />
      <StatCard
        label="Needs identified"
        value={`${latest.unique_needs_count}`}
        hint="you name what you need"
      />
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
      <div className="text-[11px] uppercase tracking-wide text-stone-500">{label}</div>
      <div className="text-2xl font-semibold text-stone-800 mt-1">{value}</div>
      <div className="text-[11px] text-stone-500 mt-1">{hint}</div>
    </div>
  );
}

function AreaChart({ weekly }) {
  const [hovered, setHovered] = useState(null);
  const svgRef = useRef(null);

  if (!weekly?.length) return null;

  const W = 640, H = 220, PAD_L = 40, PAD_R = 16, PAD_T = 16, PAD_B = 30;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const maxVal = Math.max(10, ...weekly.map(w => Math.max(w.emotion_word_count, w.unique_needs_count))) * 1.2;
  const yTicks = 3;

  const xOf = (i) => PAD_L + (weekly.length === 1 ? innerW / 2 : (i * innerW) / (weekly.length - 1));
  const yOf = (v) => PAD_T + innerH - (v / maxVal) * innerH;

  const wordPath = weekly.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(p.emotion_word_count)}`).join(' ');
  const needPath = weekly.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i)} ${yOf(p.unique_needs_count)}`).join(' ');
  const areaPath = `${wordPath} L ${xOf(weekly.length - 1)} ${yOf(0)} L ${xOf(0)} ${yOf(0)} Z`;

  const labelStep = weekly.length > 8 ? 2 : 1;

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) * (W / rect.width);
    let nearest = 0, minDist = Infinity;
    weekly.forEach((_, i) => {
      const d = Math.abs(xOf(i) - mouseX);
      if (d < minDist) { minDist = d; nearest = i; }
    });
    setHovered(minDist < innerW / weekly.length ? nearest : null);
  };

  const tip = hovered !== null ? weekly[hovered] : null;
  const tipX = tip ? Math.min(xOf(hovered), W - 168) : 0;
  const tipY = tip ? Math.max(PAD_T, yOf(tip.emotion_word_count) - 72) : 0;

  return (
    <div>
      <div className="text-sm font-medium text-stone-700 mb-2">Emotional vocabulary expansion</div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Weekly emotional vocabulary expansion"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHovered(null)}
        style={{ cursor: 'crosshair' }}
      >
        <defs>
          <linearGradient id="wordAreaFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="#6ba48a" stopOpacity="0.35"/>
            <stop offset="100%" stopColor="#6ba48a" stopOpacity="0.04"/>
          </linearGradient>
        </defs>

        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const v = Math.round((maxVal / yTicks) * i);
          const yy = yOf(v);
          return (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={yy} y2={yy} stroke="#eee7df" strokeWidth="1"/>
              <text x={PAD_L - 6} y={yy + 4} textAnchor="end" fontSize="10" fill="#a8a29e">{v}</text>
            </g>
          );
        })}

        <path d={areaPath} fill="url(#wordAreaFill)" />
        <path d={wordPath} stroke="#4a8a6f" strokeWidth="2" fill="none" strokeLinejoin="round" />
        <path d={needPath} stroke="#a78bfa" strokeWidth="1.75" fill="none" strokeDasharray="5,3" strokeLinejoin="round" />

        {weekly.map((p, i) => (
          <g key={i}>
            <circle cx={xOf(i)} cy={yOf(p.emotion_word_count)} r={hovered === i ? 5 : 3.5} fill="#4a8a6f" />
            {i % labelStep === 0 && (
              <text x={xOf(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#78716c">{fmtWeek(p.period_start)}</text>
            )}
          </g>
        ))}

        {weekly.map((_, i) => (
          <rect
            key={i}
            x={xOf(i) - innerW / (weekly.length * 2)}
            y={PAD_T}
            width={innerW / weekly.length}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHovered(i)}
          />
        ))}

        {tip && (
          <g>
            <line x1={xOf(hovered)} x2={xOf(hovered)} y1={PAD_T} y2={H - PAD_B} stroke="#c5bfb7" strokeWidth="1" strokeDasharray="3,2" />
            <rect x={tipX} y={tipY} width="162" height="62" rx="8" fill="#1c1917" opacity="0.93" />
            <text x={tipX + 12} y={tipY + 18} fontSize="11" fill="#f5f0eb" fontWeight="600">{fmtWeek(tip.period_start)}</text>
            <circle cx={tipX + 12} cy={tipY + 33} r="4" fill="#4a8a6f" />
            <text x={tipX + 22} y={tipY + 37} fontSize="10" fill="#d6d3d1">Emotion words used: {tip.emotion_word_count}</text>
            <circle cx={tipX + 12} cy={tipY + 50} r="4" fill="#a78bfa" />
            <text x={tipX + 22} y={tipY + 54} fontSize="10" fill="#d6d3d1">Needs identified: {tip.unique_needs_count}</text>
          </g>
        )}
      </svg>

      <div className="flex gap-5 text-[11px] text-stone-500 mt-2 pl-10">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-5 h-0.5 rounded bg-[#4a8a6f] inline-block"/>
          Emotion words
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-5 h-0.5 rounded bg-[#a78bfa] inline-block" style={{ borderTop: '2px dashed #a78bfa', background: 'none' }}/>
          Needs identified
        </span>
      </div>
    </div>
  );
}

function EmotionWords({ newWords, growingWords, coreWords }) {
  const total = newWords.length + growingWords.length + coreWords.length;
  if (total === 0) return null;

  const allChips = [
    ...newWords.map(w => ({ word: w, tone: 'new' })),
    ...growingWords.map(w => ({ word: w, tone: 'growing' })),
    ...coreWords.map(w => ({ word: w, tone: 'core' })),
  ];

  return (
    <div>
      <div className="text-sm font-medium text-stone-700 mb-3">Your emotional words</div>
      <div className="flex flex-wrap gap-1.5 items-center mb-3 text-[11px] text-stone-500">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"/>New this month</span>
        <span className="mx-1 text-stone-300">·</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block"/>Growing in use</span>
        <span className="mx-1 text-stone-300">·</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-400 inline-block"/>Core vocabulary</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {allChips.slice(0, 30).map(({ word, tone }) => (
          <Chip key={`${tone}-${word}`} word={word} tone={tone} />
        ))}
      </div>
    </div>
  );
}

function Chip({ word, tone }) {
  const cls = {
    new:     'bg-emerald-50 text-emerald-800 border-emerald-200',
    growing: 'bg-sky-50 text-sky-800 border-sky-200',
    core:    'bg-violet-50 text-violet-800 border-violet-200',
  }[tone];
  return (
    <span className={`px-3 py-1 rounded-full text-xs border ${cls}`}>{word}</span>
  );
}

function LanguagePairs({ pairs }) {
  if (!pairs?.length) return null;
  return (
    <div>
      <div className="text-sm font-medium text-stone-700 mb-3">How your language has evolved</div>
      <div className="divide-y divide-stone-100">
        {pairs.map((p, i) => (
          <PairCard key={i} pair={p} />
        ))}
      </div>
    </div>
  );
}

function PairCard({ pair }) {
  return (
    <div className="py-4 space-y-2">
      <Row date={pair.before_date} text={pair.before_text} />
      <Row date={pair.after_date} text={pair.after_text} accent />
    </div>
  );
}

function Row({ date, text, accent }) {
  return (
    <div className="flex gap-3 text-sm">
      <div className="w-10 shrink-0 text-xs text-stone-400 pt-0.5">
        {!accent && fmtMonth(date)}
      </div>
      <div className={accent ? 'text-stone-800' : 'text-stone-500 italic'}>
        {accent && <span className="text-xs text-stone-400 not-italic">became </span>}
        "{text}"
      </div>
    </div>
  );
}
